import { useLazyQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";

import { GridInputs, SearchForm } from "../../../../components/Functional";
import { Button, Spinner } from "../../../../components/ui";

import { FETCH_CONTACTS_LIST as fetchContactsListQuery } from "../../../../graphql/query/Contact";
import { FETCH_CONTACT_TYPES } from "../../../../graphql/query/ContactType";
import {
  FETCH_COUNTRIES,
  FETCH_CONTINENTS,
} from "../../../../graphql/query/Country";
import { FETCH_ARTISTS as fetchArtists } from "../../../../graphql/query/Artist";
import { FETCH_ARTWORK_CATEGORIES } from "../../../../graphql/query/ArtworkCategory";
import { FETCH_FOCUSES } from "../../../../graphql/query/Focus";
import { FETCH_INVOICE_ANALYSIS_EVENTS } from "../../../../graphql/query/InvoiceAnalysisEvent";
import { FETCH_MAILING_LISTS } from "../../../../graphql/query/MailingList";
import useOfferStatus from "../../../../hooks/useOfferStatus";
import useRequestTypes from "../../../../hooks/useRequestTypes";
import {
  CONTACT_PAGINATION_QUERY_PARAM,
  contactFilterStore,
  getContactPaginationFromLocationSearch,
  getContactSearchFromLocationSearch,
  serializeContactSearchToQueryParams,
  useContactFilters,
} from "../useContactFilters";

import Actions, { getActionInputs } from "./components/Actions";
import Addresses, { getAddressInputs } from "./components/Addresses";
import Affiliations, { getAffiliationInputs } from "./components/Affiliations";
import Attributes, { getAttributeInputs } from "./components/Attributes";
import Auctions, { getAuctionInputs } from "./components/Auctions";
import ClientInfo, { getClientInfoInputs } from "./components/ClientInfo";
import Collection, { getCollectionInputs } from "./components/Collection";
import ContactDetails, { getContactDetailInputs } from "./components/ContactDetails";
import Events, { getEventInputs } from "./components/Events";
import Handover, { getHandoverInputs } from "./components/Handover";
import MaterialSent, { getMaterialSentInputs } from "./components/MaterialSent";
import Offers, { getOfferInputs } from "./components/Offers";
import Query from "./components/Query";
import Requests, { getRequestInputs } from "./components/Requests";
import Sales, { getSaleInputs } from "./components/Sales";
import Sort, { getSortInputs } from "./components/Sort";
import Tabs from "./components/Tabs";
import { useEffect } from "react";
import { FETCH_COUNTIES } from "../../../../graphql/query/County";
import { useEntitiesLazy } from "../../../../hooks/useEntities";
import {
  getMultiSelectFieldConfigs,
  getOperatorFieldNames,
  hydrateMultiSelectFormikValues,
  mapParsedFilterToFormikValues,
  mergeNested,
  convertToFilter,
} from "../../../../util";

const ContactSearch = props => {
  const { closeModal, isSearchOpen = false, onSearchSubmitted } = props || {};
  const filterKeys = ["contactCategory", "tab"];
  const skipKeys = ["limit", "offset"];

  const [
    fetchContactTypesLazily,
    { loading: contactTypesLoading, data: { contactTypes = [] } = {} },
  ] = useLazyQuery(FETCH_CONTACT_TYPES);

  const [
    fetchContinentsLazily,
    { loading: continentsLoading, data: { continents = [] } = {} },
  ] = useLazyQuery(FETCH_CONTINENTS);

  const [
    fetchCountriesLazily,
    { loading: countriesLoading, data: { countries = [] } = {} },
  ] = useLazyQuery(FETCH_COUNTRIES);

  const [fetchEntitiesLazily, { loading: entitiesLoading, entities }] =
    useEntitiesLazy();

  const [
    fetchMailingListsLazily,
    {
      loading: mailingListsLoading,
      data: { mailingLists: { edges: mailingLists = [] } = {} } = {},
    },
  ] = useLazyQuery(FETCH_MAILING_LISTS);

  const [
    fetchCountiesLazily,
    { loading: countiesLoading, data: { counties = [] } = {} },
  ] = useLazyQuery(FETCH_COUNTIES);

  const [
    fetchArtistsLazily,
    { data: { artists = [] } = {} },
  ] = useLazyQuery(fetchArtists());

  const [
    fetchArtworkCategoriesLazily,
    { data: { artworkCategories = [] } = {} },
  ] = useLazyQuery(FETCH_ARTWORK_CATEGORIES);

  const [
    fetchInvoiceAnalysisEventsLazily,
    { data: { invoiceAnalysisEvents = [] } = {} },
  ] = useLazyQuery(FETCH_INVOICE_ANALYSIS_EVENTS);

  const [
    fetchFocusesLazily,
    { data: { focuses = [] } = {} },
  ] = useLazyQuery(FETCH_FOCUSES);

  const { offerStatuses = [] } = useOfferStatus();
  const { requestTypes = [] } = useRequestTypes();

  useEffect(() => {
    if (isSearchOpen) {
      fetchContactTypesLazily();
      fetchCountriesLazily();
      fetchCountiesLazily();
      fetchContinentsLazily();
      fetchEntitiesLazily();
      fetchMailingListsLazily();
      fetchArtistsLazily();
      fetchArtworkCategoriesLazily();
      fetchInvoiceAnalysisEventsLazily();
      fetchFocusesLazily();
    }
  }, [isSearchOpen]);

  const [fetchContactsV2, { loading, data: { contactsV2 } = {} }] =
    useLazyQuery(fetchContactsListQuery());
  const navigate = useNavigate();
  const location = useLocation();
  const { formik } = useContactFilters();
  const storeInput = contactFilterStore.get();

  const contactTypeOptions =
    contactTypes?.map(item => ({
      label: item?.description,
      value: item?.id,
    })) || [];
  const continentOptions =
    countries?.map(item => ({ label: item?.continent, value: item?.id })) || [];
  const countryOptions =
    countries?.map(item => ({ label: item?.name, value: item?.id })) || [];
  const entityOptions =
    entities?.edges?.map(item => ({ label: item?.name, value: item?.id })) ||
    [];
  const mailingListOptions =
    mailingLists?.map(item => ({
      label: item?.description,
      value: item?.id,
    })) || [];
  const countyOptions =
    counties?.map(item => ({
      label: `${item?.code}|${item?.description}`,
      value: item?.code,
    })) || [];

  const inputs = {
    className: "grid grid-cols-3 gap-4 mt-5",
    inputs: [
      {
        className: "grid-cols-2 gap-4",
        inputs: [
          {
            hasOperator: true,
            label: "Name",
            name: "name",
            type: "text",
            className: "col-span-2",
          },
          { label: "First name", name: "forename", type: "text" },
          { label: "Surname", name: "surname", type: "text" },
          { label: "Company", name: "company", type: "text" },
          { label: "Job title", name: "title", type: "text" },
          {
            hasOperator: true,
            label: "Address",
            name: "address",
            type: "text",
          },
          { hasOperator: true, label: "Town/City", name: "town", type: "text" },
          {
            label: "County/State/Territory",
            name: "county",
            hasOperator: true,
            isMulti: true,
            isCreatable: true,
            options: countyOptions,
            type: "multi-select",
          },
          { label: "Postcode", name: "postcode", type: "text" },
          {
            hasOperator: true,
            isMulti: true,
            label: "Country",
            name: "countryId",
            options: countryOptions,
            type: "multi-select",
          },
          {
            hasOperator: true,
            label: "Continent",
            name: "_country.continent",
            type: "multi-select",
            options: continents?.map(item => ({
              label: item?.continent,
              value: item?.continent,
            })),
          },
          // continent search uses the country table which creates issues
          // { isMulti: true, label: "Continent", name: "countryId", options: continentOptions, type: "multi-select" },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            className: "p-4 border rounded gap-4",
            inputs: [
              {
                hasOperator: true,
                isMulti: true,
                label: "Primary Sales Contact",
                name: "salesUserId",
                options: entityOptions,
                type: "multi-select",
              },
              {
                hasOperator: true,
                isMulti: true,
                label: "Secondary Sales Contact",
                name: "salesUser2Id",
                options: entityOptions,
                type: "multi-select",
              },
            ],
          },
          {
            className: "gap-4",
            inputs: [
              {
                hasOperator: true,
                isMulti: true,
                label: "Exhibitions Liason",
                name: "exhUserId",
                options: entityOptions,
                type: "multi-select",
              },
              {
                label: "Important Info",
                name: "prefered",
                type: "text",
              },
            ],
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          { allowEmpty: true, hasOperator: true, label: "ID", name: "id", type: "number" },
          {
            hasOperator: true,
            isMulti: true,
            label: "Mailing List",
            name: "_contactMailingList.mailingListId",
            options: mailingListOptions,
            type: "multi-select",
          },
          {
            hasOperator: true,
            isMulti: true,
            label: "Type",
            name: "typeId",
            options: contactTypeOptions,
            type: "multi-select",
          },
        ],
      },
    ],
  };
  const tabInputConfigs = [
    getActionInputs(),
    getAddressInputs(),
    getAffiliationInputs(),
    getAttributeInputs(),
    getAuctionInputs(),
    getClientInfoInputs(),
    getCollectionInputs({ artists, focuses }),
    getContactDetailInputs(),
    getEventInputs(),
    getHandoverInputs(),
    getMaterialSentInputs(),
    getOfferInputs({ artists, offerStatuses, entities: { edges: entities?.edges } }),
    getRequestInputs({ artists, requestTypes }),
    getSaleInputs({ artists, invoiceAnalysisEvents, artworkCategories }),
    getSortInputs(),
  ];
  const allInputs = [...inputs.inputs, ...tabInputConfigs];
  const operatorFieldNames = new Set(getOperatorFieldNames(allInputs));
  const multiSelectConfigs = getMultiSelectFieldConfigs(allInputs);

  const inputProps = {
    formik,
    ...inputs,
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return;
    }

    const parsedSearch = getContactSearchFromLocationSearch(location.search);

    if (!parsedSearch) {
      return;
    }

    const hydratedValues = mapParsedFilterToFormikValues({
      parsedFilter: parsedSearch,
      operatorFieldNames,
    });
    const hydratedMultiSelectValues = hydrateMultiSelectFormikValues({
      hydratedValues,
      multiSelectConfigs,
    });

    formik.setValues(mergeNested(formik.initialValues, hydratedMultiSelectValues));
  }, [
    isSearchOpen,
    location.search,
    contactTypeOptions.length,
    countries.length,
    counties.length,
    entities?.edges?.length,
    mailingLists.length,
    continents.length,
    artists.length,
    artworkCategories.length,
    invoiceAnalysisEvents.length,
    focuses.length,
    offerStatuses.length,
    requestTypes.length,
  ]);

  const handleSubmit = e => {
    e?.preventDefault();
    const searchInput = convertToFilter({
      values: formik.values,
      filterKeys,
      skipKeys,
    });
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams();
    nextParams.set("status", currentParams.get("status") || "all");
    const { limit: currentLimit } =
      getContactPaginationFromLocationSearch(location.search);
    nextParams.set(CONTACT_PAGINATION_QUERY_PARAM, `0,${currentLimit}`);
    ["listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          nextParams.set(key, value);
        }
      },
    );

    fetchContactsV2({
      variables: {
        input: {
          ...searchInput,
          limit: storeInput?.limit,
          offset: 0,
        },
      },
    }).then(() => {
      const contactSearchParams = serializeContactSearchToQueryParams({
        currentParams: nextParams,
        searchInput,
      });
      navigate(`/contacts?${contactSearchParams}`);
      // Performing a new search invalidates any selection from the previous
      // result set; clear it so stale ids cannot reach bulk actions like
      // Handover or Export.
      onSearchSubmitted?.();
      closeModal();
    });
  };

  const handleReset = () => {
    formik?.resetForm();
  };

  const searchButtonProps = {
    action: "black",
    label: loading ? "Searching" : "Search",
    disabled: loading,
    type: "submit",
  };

  const resetButtonProps = {
    action: "default",
    label: "Reset",
    onClick: handleReset,
  };

  const tabComponentProps = {
    artists,
    artworkCategories,
    contactTypeOptions,
    contactsV2,
    continentOptions,
    countryOptions,
    entities,
    entityOptions,
    focuses,
    formik,
    invoiceAnalysisEvents,
    offerStatuses,
    requestTypes,
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Contact Details",
        value: "contact_details",
        component: <ContactDetails {...tabComponentProps} />,
      },
      {
        label: "Addresses",
        value: "addresses",
        component: <Addresses {...tabComponentProps} />,
      },
      {
        label: "Client Info",
        value: "client_info",
        component: <ClientInfo {...tabComponentProps} />,
      },
      {
        label: "Collection",
        value: "collection",
        component: <Collection {...tabComponentProps} />,
      },
      {
        label: "Material Sent",
        value: "material_sent",
        component: <MaterialSent {...tabComponentProps} />,
      },
      {
        label: "Sales",
        value: "sales",
        component: <Sales {...tabComponentProps} />,
      },
      {
        label: "Offers",
        value: "offers",
        component: <Offers {...tabComponentProps} />,
      },
      {
        label: "Requests",
        value: "requests",
        component: <Requests {...tabComponentProps} />,
      },
      {
        label: "Affiliations",
        value: "affiliations",
        component: <Affiliations {...tabComponentProps} />,
      },
      {
        label: "Events",
        value: "events",
        component: <Events {...tabComponentProps} />,
      },
      {
        label: "Handover",
        value: "handover",
        component: <Handover {...tabComponentProps} />,
      },
      {
        label: "Actions",
        value: "actions",
        component: <Actions {...tabComponentProps} />,
      },
      {
        label: "Attributes",
        value: "attributes",
        component: <Attributes {...tabComponentProps} />,
      },
      {
        label: "Auctions",
        value: "auctions",
        component: <Auctions {...tabComponentProps} />,
      },
      {
        label: "Sort",
        value: "sort",
        component: <Sort {...tabComponentProps} />,
      },
      {
        label: "Query",
        value: "Query",
        component: <Query {...tabComponentProps} />,
      },
    ],
  };

  return (
    <SearchForm onSubmit={handleSubmit}>
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <div className="text-xl font-bold">Contact Search</div>
          <div className="flex gap-4">
            <Button {...resetButtonProps} />
            <Button {...searchButtonProps} />
          </div>
        </div>

        {contactTypesLoading ||
          countriesLoading ||
          countiesLoading ||
          continentsLoading ||
          entitiesLoading ||
          mailingListsLoading ? (
          <div className="flex h-[70dvh] w-full items-center justify-center ">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <GridInputs {...inputProps} />
            <Tabs {...tabsProps} />
          </div>
        )}
      </div>
    </SearchForm>
  );
};

export default ContactSearch;
